import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import CommonPagination from "../../components/Table/CommonPagination";
import { showToast } from "../../store/slices/common";
import routePaths from "../../router/route-paths";
import {
  getBlockedVotes,
  getDeviceClusters,
  getFlaggedUsers,
  runAntiCheatScan,
} from "../../http/antiCheat";

const PAGE_SIZE = 20;

const TABS = [
  { value: "clusters", label: "Device clusters" },
  { value: "flagged", label: "Flagged users" },
  { value: "blocked", label: "Blocked votes" },
];

const REASON_LABEL = {
  SAME_DEVICE_CONTESTANT: { label: "Voted for own device", color: "error" },
  SAME_DEVICE_DUPLICATE: { label: "Second vote from device", color: "warning" },
};

const cardSx = {
  padding: "20px",
  borderRadius: "10px",
  boxShadow:
    "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  boxSizing: "border-box",
};

const headCellSx = {
  fontWeight: 700,
  color: "#142C5C",
  backgroundColor: "#f4f6fb",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatPercent = (ratio) =>
  typeof ratio === "number" && Number.isFinite(ratio)
    ? `${Math.round(ratio * 100)}%`
    : "—";

const shortDevice = (deviceId) =>
  deviceId && deviceId.length > 14 ? `${deviceId.slice(0, 8)}…${deviceId.slice(-4)}` : deviceId || "—";

const extractErrorMessage = (err) =>
  err?.response?.data?.message || err?.message || "Request failed";

/** Account state chip: deleted / blocked / suspended / active. */
const AccountStateChip = ({ user }) => {
  if (!user) return <Chip size="small" label="Unknown" />;
  if (user.isDeleted && user.isAccountBlocked) {
    return <Chip size="small" color="error" label="Banned" />;
  }
  if (user.isDeleted) return <Chip size="small" label="Deleted" />;
  if (user.isAccountBlocked) return <Chip size="small" color="error" label="Blocked" />;
  if (user.bannedUntil && new Date(user.bannedUntil) > new Date()) {
    return <Chip size="small" color="warning" label="Suspended" />;
  }
  return <Chip size="small" color="success" variant="outlined" label="Active" />;
};

/**
 * Username with avatar; clicking opens Account Tools prefilled for ban /
 * suspend. The secondary link opens the View User vote page.
 */
const UserCell = ({ user, fallbackId }) => {
  const navigate = useNavigate();
  const id = user?._id || fallbackId;
  if (!user?.username) {
    return (
      <Typography sx={{ fontSize: "12px", color: "#999" }}>
        {id ? `(${String(id).slice(-6)})` : "—"}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Avatar src={user.profilePicture || undefined} sx={{ width: 28, height: 28 }}>
        {user.username[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Link
          component="button"
          underline="hover"
          sx={{ fontSize: "13px", fontWeight: 600, textAlign: "left" }}
          onClick={() =>
            navigate(`${routePaths.USER_MODIFICATION}?username=${encodeURIComponent(user.username)}`)
          }
          title="Open in Account Tools"
        >
          @{user.username}
        </Link>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <AccountStateChip user={user} />
          {id && (
            <Link
              component="button"
              underline="hover"
              sx={{ fontSize: "11px" }}
              onClick={() => navigate(routePaths.VIEW_USER.replace(":id", id))}
            >
              votes
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const EmptyRow = ({ colSpan, text }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ textAlign: "center", py: 6, color: "#777" }}>
      {text}
    </TableCell>
  </TableRow>
);

// ─── Device clusters ──────────────────────────────────────────────────────

const ClustersTab = ({ refreshToken, onError }) => {
  const [page, setPage] = useState(0);
  const [minAccounts, setMinAccounts] = useState(2);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [maxAccounts, setMaxAccounts] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => setPage(0), [minAccounts]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await getDeviceClusters({ page: page + 1, limit: PAGE_SIZE, minAccounts });
        if (cancelled) return;
        const data = response?.data || {};
        setRows(data.clusters || []);
        setTotal(data.totalCount || 0);
        setMaxAccounts(data.maxAccountsPerDevice ?? null);
      } catch (err) {
        if (!cancelled) onError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, minAccounts, refreshToken, onError]);

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <TextField
          select
          size="small"
          label="Accounts on install"
          value={minAccounts}
          onChange={(e) => setMinAccounts(Number(e.target.value))}
          sx={{ minWidth: 180 }}
        >
          {[2, 3, 4, 5].map((n) => (
            <MenuItem key={n} value={n}>
              {n}+
            </MenuItem>
          ))}
        </TextField>
        {maxAccounts != null && (
          <Typography sx={{ fontSize: "13px", color: "#666" }}>
            Signup cap: {maxAccounts} live account{maxAccounts === 1 ? "" : "s"} per install.
            Installs above the cap predate the rule or freed a slot by deleting an account.
          </Typography>
        )}
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e6e8f0" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Install</TableCell>
              <TableCell sx={headCellSx}>Accounts</TableCell>
              <TableCell sx={headCellSx}>Votes between them</TableCell>
              <TableCell sx={headCellSx}>Last seen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyRow colSpan={4} text={loading ? "Loading…" : "No installs with that many linked accounts"} />
            ) : (
              rows.map((cluster) => (
                <TableRow key={cluster.deviceId} hover>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Tooltip title={cluster.deviceId}>
                      <Typography sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                        {shortDevice(cluster.deviceId)}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: "11px", color: "#888" }}>
                      {cluster.platform || "unknown"} · {cluster.accountCount} accounts
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {cluster.accounts.map((u) => (
                        <UserCell key={u._id} user={u} fallbackId={u._id} />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    {cluster.votesExchanged > 0 ? (
                      <>
                        <Chip
                          size="small"
                          color="error"
                          label={`${cluster.votesExchanged} vote${cluster.votesExchanged === 1 ? "" : "s"}`}
                          sx={{ mb: 0.5 }}
                        />
                        {cluster.votePairs.map((pair) => {
                          const voter = cluster.accounts.find((a) => String(a._id) === String(pair.voterId));
                          const target = cluster.accounts.find((a) => String(a._id) === String(pair.votedFor));
                          return (
                            <Typography key={`${pair.voterId}-${pair.votedFor}`} sx={{ fontSize: "12px" }}>
                              @{voter?.username || "?"} → @{target?.username || "?"}: {pair.votes}
                            </Typography>
                          );
                        })}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: "12px", color: "#999" }}>none</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top", fontSize: "12px" }}>
                    {formatDate(cluster.lastSeenAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {total > PAGE_SIZE && (
        <Box sx={{ mt: 2 }}>
          <CommonPagination rowsPerPage={PAGE_SIZE} count={total} page={page} onPageChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </>
  );
};

// ─── Flagged users ────────────────────────────────────────────────────────

const FlaggedTab = ({ refreshToken, onError }) => {
  const [page, setPage] = useState(0);
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({ lastScan: null, thresholds: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => setPage(0), [flaggedOnly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await getFlaggedUsers({ page: page + 1, limit: PAGE_SIZE, flaggedOnly });
        if (cancelled) return;
        const data = response?.data || {};
        setRows(data.reports || []);
        setTotal(data.totalCount || 0);
        setMeta({ lastScan: data.lastScan || null, thresholds: data.thresholds || null });
      } catch (err) {
        if (!cancelled) onError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, flaggedOnly, refreshToken, onError]);

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <FormControlLabel
          control={<Switch checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} />}
          label="Flagged only"
        />
        <Typography sx={{ fontSize: "13px", color: "#666" }}>
          {meta.thresholds
            ? `Flagged = ${meta.thresholds.minLinkedVotes}+ votes from device-linked accounts and ≥${Math.round(
                meta.thresholds.ratio * 100
              )}% of votes received in the last ${meta.thresholds.lookbackDays} days.`
            : ""}
          {meta.lastScan ? ` Last scan ${formatDate(meta.lastScan.scannedAt)}.` : " No scan has run yet."}
        </Typography>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e6e8f0" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>User</TableCell>
              <TableCell sx={headCellSx}>Linked votes</TableCell>
              <TableCell sx={headCellSx}>Share</TableCell>
              <TableCell sx={headCellSx}>Matches</TableCell>
              <TableCell sx={headCellSx}>Voting accounts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyRow colSpan={5} text={loading ? "Loading…" : "Nothing flagged"} />
            ) : (
              rows.map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <UserCell user={r.user} fallbackId={r.userId} />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                      {r.linkedVotesReceived} / {r.totalVotesReceived}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#888" }}>linked / total received</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Chip
                      size="small"
                      color={r.flagged ? "error" : "default"}
                      label={formatPercent(r.ratio)}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top", fontSize: "13px" }}>{r.affectedMatchCount}</TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {(r.clusters || []).map((c) => (
                        <Box key={String(c.voterId)} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <UserCell user={c.voter} fallbackId={c.voterId} />
                          <Typography sx={{ fontSize: "12px", color: "#555" }}>
                            {c.votes} vote{c.votes === 1 ? "" : "s"} ·{" "}
                            <Tooltip title={(c.deviceIds || []).join("\n")}>
                              <span>{(c.deviceIds || []).length} shared install{(c.deviceIds || []).length === 1 ? "" : "s"}</span>
                            </Tooltip>
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {total > PAGE_SIZE && (
        <Box sx={{ mt: 2 }}>
          <CommonPagination rowsPerPage={PAGE_SIZE} count={total} page={page} onPageChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </>
  );
};

// ─── Blocked votes ────────────────────────────────────────────────────────

const BlockedTab = ({ refreshToken, onError }) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await getBlockedVotes({ page: page + 1, limit: PAGE_SIZE });
        if (cancelled) return;
        const data = response?.data || {};
        setRows(data.blockedVotes || []);
        setTotal(data.totalCount || 0);
      } catch (err) {
        if (!cancelled) onError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, refreshToken, onError]);

  return (
    <>
      <Typography sx={{ fontSize: "13px", color: "#666", mb: 2 }}>
        Vote attempts refused at the API because the install was linked to a contestant, or another
        account on the same install had already voted on that pagent. None of these changed a match.
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e6e8f0" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>When</TableCell>
              <TableCell sx={headCellSx}>Voter</TableCell>
              <TableCell sx={headCellSx}>Voted for</TableCell>
              <TableCell sx={headCellSx}>Reason</TableCell>
              <TableCell sx={headCellSx}>Linked account</TableCell>
              <TableCell sx={headCellSx}>Install</TableCell>
              <TableCell sx={headCellSx}>Match</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7} text={loading ? "Loading…" : "No blocked votes yet"} />
            ) : (
              rows.map((r) => {
                const reason = REASON_LABEL[r.reason] || { label: r.reason, color: "default" };
                return (
                  <TableRow key={r._id} hover>
                    <TableCell sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>{formatDate(r.createdAt)}</TableCell>
                    <TableCell>
                      <UserCell user={r.voter} fallbackId={r.voterId} />
                    </TableCell>
                    <TableCell>
                      <UserCell user={r.votedForUser} fallbackId={r.votedFor} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={reason.color} label={reason.label} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {(r.linkedUsers || []).map((u) => (
                          <UserCell key={String(u._id)} user={u} fallbackId={u._id} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.deviceId}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                          {shortDevice(r.deviceId)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px" }}>
                      {r.matchType || "—"}
                      <Typography sx={{ fontSize: "11px", color: "#888", fontFamily: "monospace" }}>
                        {String(r.matchId).slice(-8)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {total > PAGE_SIZE && (
        <Box sx={{ mt: 2 }}>
          <CommonPagination rowsPerPage={PAGE_SIZE} count={total} page={page} onPageChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────

/**
 * Anti-Cheat: multi-account vote abuse. Device clusters come straight from
 * the account↔install links; Flagged users is the nightly scan; Blocked votes
 * are attempts the API refused. Every username opens Account Tools for
 * ban / suspend.
 */
const AntiCheat = () => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState("clusters");
  const [refreshToken, setRefreshToken] = useState(0);
  const [scanning, setScanning] = useState(false);

  const onError = useCallback(
    (err) => {
      console.error("Anti-cheat request failed:", err);
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    },
    [dispatch]
  );

  const handleScan = async () => {
    setScanning(true);
    try {
      const response = await runAntiCheatScan();
      const s = response?.data || {};
      dispatch(
        showToast({
          color: "success",
          msg: `Scan complete: ${s.linkedVotes ?? 0} linked votes, ${s.reports ?? 0} reports (${s.flagged ?? 0} flagged).`,
        })
      );
      setRefreshToken((t) => t + 1);
    } catch (err) {
      onError(err);
    } finally {
      setScanning(false);
    }
  };

  return (
    <PageLayout
      title="Anti-Cheat"
      subtitle="Accounts sharing an app install, and votes exchanged between them. Signups are capped per install and same-device votes are refused at the API; this page shows what those rules caught and what slipped through before them."
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setRefreshToken((t) => t + 1)}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleScan}
            disabled={scanning}
          >
            {scanning ? "Scanning…" : "Run scan now"}
          </Button>
        </Box>
      }
    >
      <Paper sx={cardSx}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, minHeight: 40 }}>
          {TABS.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              label={t.label}
              sx={{ minHeight: 40, textTransform: "none", fontWeight: 600 }}
            />
          ))}
        </Tabs>
        {tab === "clusters" && <ClustersTab refreshToken={refreshToken} onError={onError} />}
        {tab === "flagged" && <FlaggedTab refreshToken={refreshToken} onError={onError} />}
        {tab === "blocked" && <BlockedTab refreshToken={refreshToken} onError={onError} />}
      </Paper>
    </PageLayout>
  );
};

export default AntiCheat;
